import type {Component, PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export interface ToggleSwitchProps extends PropertyProps {
  component?: Component;
  defaultValue?: boolean; // Renamed to avoid conflict with PropertyProps.default
  attribute?: string;
  label: string;
  onToggleRight: () => void;
  onToggleLeft: () => void;
  callback?: (tooltip: Element) => void;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  /**
   * Allow to toggle between two options
   */
  styleManager.addType(window.PMW.emails.styles.ID_TOGGLE_SWITCH, {
    create(data) {
      const {change} = data;
      const props = data.props as ToggleSwitchProps;

      const currComponent = editor.runCommand(window.PMW.emails.commands.GET_COMPONENT_FOR_TOGGLE_SWITCH, {component: props.component}) as Component;
      const toggleSelector = document.createElement('div');
      const attributes = currComponent.getAttributes() as Record<string, unknown> || {};

      let isToggled = false;
      if ((props.attribute && attributes[props.attribute]) || props.defaultValue) {
        isToggled = true;
      }

      toggleSelector.className = props.type ?? '';
      toggleSelector.innerHTML = getToggleSwitchHtml(props, isToggled);
      const tooltip = toggleSelector.getElementsByClassName('react-tooltip')[0];
      attachListenerPresetToggleButtonSelector(props, change, toggleSelector);

      if (props.callback) {
        props.callback(tooltip);
      }

      return toggleSelector;
    },

    emit() {
      // No-op
    }
  });

  /**
   * Attaches event listeners to toggle switch
   */
  function attachListenerPresetToggleButtonSelector(
    props: ToggleSwitchProps,
    change: (data: {toggleContainer: HTMLElement; isToggled: boolean}) => void,
    toggleContainer: HTMLElement
  ): void {
    const toggleElement = toggleContainer.querySelector('.tgl');
    if (!toggleElement) return;

    const inputElement = toggleElement as HTMLInputElement;
    inputElement.addEventListener('change', function (event) {
      const target = event.target as HTMLInputElement;
      if (target.checked) {
        props.onToggleRight();
      } else {
        props.onToggleLeft();
      }
      change({toggleContainer, isToggled: target.checked});
    });
  }

  /**
   * Generates HTML markup for toggle switch
   */
  function getToggleSwitchHtml(props: ToggleSwitchProps, isToggled = false): string {
    return `
            <div class="toggle-container spacing-m-0">
                <div class="flexbox flex-justify-between flex-align-items-center">
                    <label class="toggle-text body-xs">${props.label}</label>
                    <div class="react-tooltip spacing-m-l-2"></div>
                </div>
                <div class="toggle">
                    <input id="js-column-section-responsive-chk" class="tgl" type="checkbox" aria-label="${window.i18next.t('pmwjs_stack_layout')}" ${isToggled ? 'checked' : ''}/>
                    <label for="js-column-section-responsive-chk" class="tgl-btn" aria-label="${window.i18next.t('pmwjs_stack_layout')}"></label>
                </div>
            </div>
        `;
  }
};